import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import profile_img from '../../assets/images/pages/defaultImage.jpg';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi from '../../utils/henceforthApi';
import loginSuccess from '../../context/actions/auth/loginSuccess';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import BreadCrumb from '../../components/common/BreadCrumb';
import { documentId, validateEmail, NumberValidation } from '../../utils/validations';
import { toast } from 'react-toastify';
import { Button } from '../../components/ui/button';
import {
  User,
  Mail,
  Building2,
  MapPin,
  Camera,
  Upload,
  X,
  FileText,
  CheckCircle2,
  AlertCircle,
  Truck,
  ArrowLeft,
  Save,
  Check,
} from 'lucide-react';

const EditProfile: React.FC = () => {
  const { language_data, authState, authDispatch, loading, setLoading, toastMessage } =
    useContext(GlobalContext);
  const navigate = useNavigate();

  const breadCrumbPath = [
    { name: `${language_data?.[authState.lang]?.common_home || 'Home'}`, url: `/`, active: '' },
    { name: `${language_data?.[authState.lang]?.common_profile || 'Profile'}`, url: `/profile`, active: '' },
    { name: `${language_data?.[authState.lang]?.common_edit || 'Edit'} Profile`, url: ``, active: 'not-allowed' },
  ];

  const [nameErr, setNameErr] = useState<string>('');
  const [emailErr, setEmailErr] = useState<string>('');
  const [phoneEr, setPhoneEr] = useState<string>('');
  const [cityErr, setCityErr] = useState<string>('');
  const [stateErr, setStateErr] = useState<string>('');
  const [companyErr, setCompanyErr] = useState<string>('');
  const [pinCodeErr, setPinCodeErr] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<any>();
  const [fullAddressErr, setFullAddressErr] = useState<string>('');
  const [apartmentNoErr, setApartmentNoErr] = useState<string>('');
  const placeInputRef = useRef(null) as React.MutableRefObject<any>;

  // Document states
  const [newDocumentFiles, setNewDocumentFiles] = useState<{ [key: string]: { front?: File; back?: File } }>({});
  const [removedDocuments, setRemovedDocuments] = useState<string[]>([]);
  const [documentTypes, setDocumentTypes] = useState<Array<{ type: string; label: string }>>([]);

  // Fetch document types from API
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const response = await henceforthApi.Auth.getDocumentTypes();
        if (response && response.data && Array.isArray(response.data)) {
          setDocumentTypes(response.data);
        } else {
          setDocumentTypes([
            { type: 'aadhar_card', label: 'Aadhar Card' },
            { type: 'pan_card', label: 'PAN Card' },
          ]);
        }
      } catch (error) {
        console.error('Error fetching document types:', error);
        setDocumentTypes([
          { type: 'aadhar_card', label: 'Aadhar Card' },
          { type: 'pan_card', label: 'PAN Card' },
        ]);
      }
    };

    fetchDocumentTypes();
  }, []);

  const [updateProfile, setUpdateProfile] = useState({
    name: authState.name ?? '',
    email: authState.email ?? '',
    phone_number: authState.phone_number ?? '',
    country_code: authState.country_code ?? '',
    company: authState.company ?? '',
    state: authState.state ?? '',
    city: authState.city ?? '',
    pin_code: authState.pin_code ?? '',
    apartment_number: authState.apartment_number ?? '',
    full_address: authState.full_address ?? '',
    image: authState.image ?? '',
    latitude: '',
    longitude: '',
    delivery_provide: authState.delivery_provide ?? false,
  });

  const getImageUrl = (image: string | null | undefined) => {
    if (!image) return profile_img;
    return image.endsWith('.webp')
      ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${image}`
      : `${henceforthApi.API_FILE_ROOT_MEDIUM}${image}`;
  };

  const getDocumentImageUrl = (image: string | null | undefined) => {
    if (!image) return profile_img;
    return image.endsWith('.webp')
      ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${image}`
      : `${henceforthApi.API_FILE_ROOT_MEDIUM}${image}`;
  };

  const changeProfileDetails = ({ target }: any) => {
    const val = target.value;
    const name = target.name;
    if (name === 'name') setNameErr('');
    if (name === 'email') setEmailErr('');
    if (name === 'company') setCompanyErr('');
    if (name === 'state') setStateErr('');
    if (name === 'city') setCityErr('');
    if (name === 'pin_code' && !NumberValidation(val)) return;
    if (name === 'apartment_number') setApartmentNoErr('');
    if (name === 'full_address') setFullAddressErr('');
    setUpdateProfile({
      ...updateProfile,
      [name]: val,
    });
  };

  const fileUpload = async (file: any) => {
    setLoading(true);
    const apiRes = await henceforthApi.Common.do_spaces_file_upload('file', file);
    return apiRes.data.file_name;
  };

  const onKeyDown = (keyEvent: any) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  const editProfile = async (e: any) => {
    e.preventDefault();
    const {
      name,
      country_code,
      phone_number,
      company,
      city,
      pin_code,
      apartment_number,
      email,
      full_address,
      state,
      delivery_provide,
    } = updateProfile;

    if (!name.trim()) {
      documentId('name');
      setNameErr('Full Name is required');
      return;
    }
    if (!email.trim()) {
      documentId('email');
      setEmailErr('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      documentId('email');
      setEmailErr('Please provide a valid email address');
      return;
    }
    if (!phone_number) {
      documentId('phone');
      toast.warn('Phone number is required');
      setPhoneEr('Phone number is required');
      return;
    }
    if (phone_number.length < 10 || phone_number.length > 12) {
      documentId('phone');
      toast.warn('Please provide a valid phone number');
      setPhoneEr('Please provide a valid phone number');
      return;
    }
    if (!company.trim()) {
      documentId('company');
      setCompanyErr('Company / Store name is required');
      return;
    }
    if (!full_address.trim()) {
      documentId('full_address');
      setFullAddressErr('Business address is required');
      return;
    }
    if (!state.trim()) {
      documentId('state');
      setStateErr('State is required');
      return;
    }
    if (!city.trim()) {
      documentId('city');
      setCityErr('City is required');
      return;
    }
    if (!pin_code.trim()) {
      documentId('pin_code');
      setPinCodeErr('Postal / Pin code is required');
      return;
    }

    setLoading(true);

    let { latitude, longitude } = updateProfile;
    if (!latitude || !longitude) {
      const coordinates = await handleAddressGeocode();
      if (coordinates) {
        latitude = coordinates.lat;
        longitude = coordinates.lng;
      }
    }

    const data: any = {
      name: name.trim(),
      phone_number,
      country_code,
      company: company.trim(),
      city: city.trim(),
      state: state.trim(),
      pin_code: pin_code.trim(),
      full_address: full_address.trim(),
      delivery_provide,
      language: 'ENGLISH',
    };

    if (apartment_number.trim()) {
      data.apartment_number = apartment_number.trim();
    }
    if (latitude && longitude) {
      data.latitude = latitude;
      data.longitude = longitude;
    }
    if (!authState.email_verified) {
      data.email = email.trim();
    }
    if (selectedFile) {
      try {
        const image = await fileUpload(selectedFile);
        data.image = image;
      } catch (err) {
        console.error('Image upload failed', err);
      }
    }

    // Process documents
    const allDocuments: any[] = [];

    if ((authState as any)?.documents && (authState as any).documents.length > 0) {
      const uploadedDocuments = await Promise.all(
        (authState as any).documents.map(async (doc: any) => {
          let frontKey = doc.images?.front || '';
          let backKey = doc.images?.back || '';

          if (newDocumentFiles[doc._id]?.front) {
            const frontUploadRes = await henceforthApi.Common.do_spaces_file_upload(
              'file',
              newDocumentFiles[doc._id].front!
            );
            frontKey = frontUploadRes.data.file_name;
          }

          if (newDocumentFiles[doc._id]?.back) {
            const backUploadRes = await henceforthApi.Common.do_spaces_file_upload(
              'file',
              newDocumentFiles[doc._id].back!
            );
            backKey = backUploadRes.data.file_name;
          }

          return {
            _id: doc._id,
            type: doc.type,
            images: {
              front: frontKey,
              back: backKey,
            },
            removed: removedDocuments.filter((r) => r.startsWith(`${doc._id}-`)),
          };
        })
      );

      allDocuments.push(...uploadedDocuments);
    }

    const newDocTypes = documentTypes.filter(
      (docType) => !(authState as any)?.documents?.find((d: any) => d.type === docType.type)
    );

    if (newDocTypes.length > 0) {
      const newUploadedDocs = await Promise.all(
        newDocTypes.map(async (docType) => {
          let frontKey = '';
          let backKey = '';

          if (newDocumentFiles[docType.type]?.front) {
            const frontUploadRes = await henceforthApi.Common.do_spaces_file_upload(
              'file',
              newDocumentFiles[docType.type].front!
            );
            frontKey = frontUploadRes.data.file_name;
          }

          if (newDocumentFiles[docType.type]?.back) {
            const backUploadRes = await henceforthApi.Common.do_spaces_file_upload(
              'file',
              newDocumentFiles[docType.type].back!
            );
            backKey = backUploadRes.data.file_name;
          }
          if (frontKey || backKey) {
            return {
              type: docType.type,
              images: {
                front: frontKey,
                back: backKey,
              },
            };
          }
          return null;
        })
      );
      allDocuments.push(...newUploadedDocs.filter((doc) => doc !== null));
    }

    if (allDocuments.length > 0) {
      data.documents = allDocuments;
    }

    try {
      const res = await henceforthApi.Auth.editProfile(data);
      loginSuccess(res.data)(authDispatch);
      toastMessage(
        res.message ? res.message : `${language_data?.[authState.lang]?.common_success || 'Profile updated successfully'}`
      );
      navigate('/profile');
    } catch (err) {
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  const phoneCheck = (e: any, v: any) => {
    setPhoneEr('');
    setUpdateProfile({
      ...updateProfile,
      phone_number: e.phone.slice(Number(v.dialCode.length), Number(e.phone.length)),
      country_code: v.dialCode,
    });
  };

  function loadGoogleMapScript(callback: any) {
    if (typeof (window as any).google === 'object' && typeof (window as any).google.maps === 'object') {
      callback();
    } else {
      const googleMapScript = document.createElement('script');
      googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD1CuR295O6gnkuW1tHdPMjqFpQewhAsD4&libraries=places`;
      window.document.body.appendChild(googleMapScript);
      googleMapScript.addEventListener('load', callback);
    }
  }

  const geocodeAddress = async (address: string): Promise<{ lat: string; lng: string } | null> => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=AIzaSyD1CuR295O6gnkuW1tHdPMjqFpQewhAsD4`
      );
      const data = await response.json();
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        if (data.results[0].partial_match) {
          return null;
        }
        const location = data.results[0].geometry.location;
        return { lat: location.lat.toString(), lng: location.lng.toString() };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return null;
  };

  const handleAddressGeocode = async (): Promise<{ lat: string; lng: string } | null> => {
    const { full_address, city, state, pin_code } = updateProfile;
    if (city && state) {
      const addressString = [city, state, pin_code, full_address].filter(Boolean).join(', ');
      const coordinates = await geocodeAddress(addressString);
      if (coordinates) {
        setUpdateProfile((prev: any) => ({
          ...prev,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        }));
        return coordinates;
      }
    }
    return null;
  };

  const initPlaceAPI = () => {
    loadGoogleMapScript(() => {
      if (placeInputRef.current) {
        const autocomplete = new (window as any).google.maps.places.Autocomplete(placeInputRef.current);

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          const formatAddress = place.formatted_address;
          const lat = place.geometry?.location?.lat();
          const lng = place.geometry?.location?.lng();

          const address = place.address_components;
          const items: any = {};

          if (Array.isArray(address)) {
            address.forEach((comp: any) => {
              if (comp.types.includes('postal_code')) {
                items.pin_code = comp.long_name;
              }
              if (comp.types.includes('administrative_area_level_1')) {
                items.state = comp.long_name;
              }
              if (comp.types.includes('locality')) {
                items.city = comp.long_name;
              }
              if (comp.types.includes('street_number')) {
                items.apartment_number = comp.long_name;
              }
            });
          }

          items.full_address = formatAddress;
          items.latitude = lat;
          items.longitude = lng;

          setUpdateProfile((prev: any) => ({
            ...prev,
            city: items.city || '',
            state: items.state || '',
            pin_code: items.pin_code || '',
            apartment_number: items.apartment_number || '',
            full_address: items.full_address || '',
            latitude: items.latitude,
            longitude: items.longitude,
          }));

          setCityErr('');
          setStateErr('');
          setPinCodeErr('');
          setApartmentNoErr('');
          setFullAddressErr('');
        });
      }
    });
  };

  useEffect(() => {
    initPlaceAPI();
  }, []);

  const handleDocumentUpload = (docId: string, side: 'front' | 'back', file: File) => {
    setNewDocumentFiles((prev) => ({
      ...prev,
      [docId]: {
        ...(prev[docId] || {}),
        [side]: file,
      },
    }));
  };

  const removeNewDocument = (docId: string, side: 'front' | 'back') => {
    setNewDocumentFiles((prev) => {
      const updated = { ...(prev[docId] || {}) };
      delete updated[side];
      return { ...prev, [docId]: updated };
    });
  };

  const removeExistingDocument = (docId: string, side: 'front' | 'back') => {
    setRemovedDocuments((prev) => [...prev, `${docId}-${side}`]);
  };

  return (
    <Fragment>
      {/* Standard Breadcrumb Bar */}
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-5">
        {/* Header Card */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
              {language_data?.[authState.lang]?.common_edit || 'Edit'} Profile
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
              Update your business info, physical store location, and identity compliance files.
            </p>
          </div>

          <Button variant="outline" size="sm" asChild>
            <Link to="/profile" className="inline-flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" />
              Back to Profile
            </Link>
          </Button>
        </div>

        {/* Main Form Form */}
        <form onKeyDown={onKeyDown} onSubmit={editProfile} className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left Column: Avatar & Store Settings */}
            <div className="space-y-5">
              {/* Profile Photo Card */}
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 space-y-4 text-center">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block text-left">
                  Profile Photo
                </span>

                <div className="flex flex-col items-center">
                  <div className="relative group cursor-pointer">
                    <img
                      src={
                        selectedFile
                          ? URL.createObjectURL(selectedFile)
                          : getImageUrl(authState?.image)
                      }
                      alt={authState?.image || 'Profile'}
                      className="w-28 h-28 rounded-2xl object-cover border-4 border-slate-100 shadow-md"
                    />
                    <label
                      htmlFor="profile_image_input"
                      className="absolute inset-0 bg-slate-900/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer"
                    >
                      <Camera className="w-6 h-6 mb-1" />
                      <span className="text-[10px] font-bold">Change</span>
                    </label>
                    <input
                      id="profile_image_input"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e: any) => e.target.files?.[0] && setSelectedFile(e.target.files[0])}
                    />
                  </div>

                  <p className="text-xs text-slate-400 font-medium mt-3 mb-0">
                    PNG, JPG or WEBP (Max 5MB)
                  </p>
                </div>
              </div>

              {/* Delivery Settings Card */}
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-[#1AA7DE]" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    {language_data?.[authState.lang]?.common_home_delivery_provide || 'Home Delivery Option'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 font-medium m-0">
                  Do you provide door-to-door delivery service for customer orders?
                </p>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <label
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border font-bold text-xs cursor-pointer transition-all ${updateProfile.delivery_provide === true
                        ? 'bg-sky-50 border-[#1AA7DE] text-[#1AA7DE]'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                  >
                    <input
                      type="radio"
                      name="deliveryProvider"
                      className="hidden"
                      checked={updateProfile.delivery_provide === true}
                      onChange={() => setUpdateProfile({ ...updateProfile, delivery_provide: true })}
                    />
                    <Check className={`w-3.5 h-3.5 ${updateProfile.delivery_provide === true ? 'inline' : 'hidden'}`} />
                    Yes, Provide
                  </label>

                  <label
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border font-bold text-xs cursor-pointer transition-all ${updateProfile.delivery_provide === false
                        ? 'bg-sky-50 border-[#1AA7DE] text-[#1AA7DE]'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                  >
                    <input
                      type="radio"
                      name="deliveryProvider"
                      className="hidden"
                      checked={updateProfile.delivery_provide === false}
                      onChange={() => setUpdateProfile({ ...updateProfile, delivery_provide: false })}
                    />
                    <Check className={`w-3.5 h-3.5 ${updateProfile.delivery_provide === false ? 'inline' : 'hidden'}`} />
                    No, Pickup Only
                  </label>
                </div>
              </div>
            </div>

            {/* Right Column: Contact & Business Info */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 space-y-4">
                <h3 className="text-base font-black text-slate-900 pb-2 border-b border-slate-100 m-0">
                  Business & Owner Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Full Name */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 block">
                      Owner Full Name <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={updateProfile.name}
                        onChange={changeProfileDetails}
                        placeholder="e.g. John Doe"
                        className={`w-full pl-9 pr-3 py-2 text-xs sm:text-sm font-medium rounded-xl border ${nameErr ? 'border-rose-400 bg-rose-50/20' : 'border-slate-200 bg-slate-50/50'
                          } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                      />
                    </div>
                    {nameErr && <p className="text-[11px] font-semibold text-rose-500 m-0">{nameErr}</p>}
                  </div>

                  {/* Company / Store Name */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 block">
                      Store / Company Name <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <Building2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        id="company"
                        type="text"
                        name="company"
                        value={updateProfile.company}
                        onChange={changeProfileDetails}
                        placeholder="e.g. Acme Retailers"
                        className={`w-full pl-9 pr-3 py-2 text-xs sm:text-sm font-medium rounded-xl border ${companyErr ? 'border-rose-400 bg-rose-50/20' : 'border-slate-200 bg-slate-50/50'
                          } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                      />
                    </div>
                    {companyErr && (
                      <p className="text-[11px] font-semibold text-rose-500 m-0">{companyErr}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                      <span>Email Address <span className="text-rose-500">*</span></span>
                      {authState.email_verified && (
                        <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                          Verified (Locked)
                        </span>
                      )}
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={updateProfile.email}
                        onChange={changeProfileDetails}
                        disabled={authState.email_verified}
                        placeholder="seller@domain.com"
                        className={`w-full pl-9 pr-3 py-2 text-xs sm:text-sm font-medium rounded-xl border ${emailErr ? 'border-rose-400 bg-rose-50/20' : 'border-slate-200 bg-slate-50/50'
                          } ${authState.email_verified ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''} focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                      />
                    </div>
                    {emailErr && <p className="text-[11px] font-semibold text-rose-500 m-0">{emailErr}</p>}
                  </div>

                  {/* Phone */}
                  <div className="space-y-1" id="phone">
                    <label className="text-xs font-bold text-slate-700 block">
                      Phone Number <span className="text-rose-500">*</span>
                    </label>
                    <div className="phone-input-wrapper">
                      <PhoneInput
                        country={'in'}
                        value={updateProfile.country_code.concat(updateProfile.phone_number)}
                        onChange={(phone: any, value: any) => phoneCheck({ phone }, value)}
                        inputStyle={{
                          width: '100%',
                          height: '38px',
                          borderRadius: '0.75rem',
                          borderColor: phoneEr ? '#f87171' : '#e2e8f0',
                          backgroundColor: '#f8fafc',
                          fontSize: '13px',
                          fontWeight: '500',
                        }}
                        buttonStyle={{
                          borderRadius: '0.75rem 0 0 0.75rem',
                          borderColor: phoneEr ? '#f87171' : '#e2e8f0',
                          backgroundColor: '#f8fafc',
                        }}
                      />
                    </div>
                    {phoneEr && <p className="text-[11px] font-semibold text-rose-500 m-0">{phoneEr}</p>}
                  </div>
                </div>

                {/* Physical Store Address Section */}
                <div className="pt-4 border-t border-slate-100 space-y-4">
                  <h4 className="text-sm font-black text-slate-900 m-0">
                    Store Location & Address
                  </h4>

                  {/* Autocomplete Search */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 block">
                      Search Address on Google Maps
                    </label>
                    <div className="relative">
                      <MapPin className="w-4 h-4 text-[#1AA7DE] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        ref={placeInputRef}
                        type="text"
                        placeholder="Start typing your street address..."
                        className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm font-medium rounded-xl border border-slate-200 bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all"
                      />
                    </div>
                  </div>

                  {/* Full Address Textarea */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 block">
                      Complete Street Address <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      id="full_address"
                      name="full_address"
                      rows={2}
                      value={updateProfile.full_address}
                      onChange={changeProfileDetails}
                      placeholder="Street name, landmark, building..."
                      className={`w-full p-3 text-xs sm:text-sm font-medium rounded-xl border ${fullAddressErr ? 'border-rose-400 bg-rose-50/20' : 'border-slate-200 bg-slate-50/50'
                        } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                    />
                    {fullAddressErr && (
                      <p className="text-[11px] font-semibold text-rose-500 m-0">{fullAddressErr}</p>
                    )}
                  </div>

                  {/* City, State, Pin Code, Apartment */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-700 block">
                        Apt / Suite #
                      </label>
                      <input
                        type="text"
                        name="apartment_number"
                        value={updateProfile.apartment_number}
                        onChange={changeProfileDetails}
                        placeholder="Suite 101"
                        className="w-full px-3 py-2 text-xs sm:text-sm font-medium rounded-xl border border-slate-200 bg-slate-50/50 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-700 block">
                        City <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="city"
                        type="text"
                        name="city"
                        value={updateProfile.city}
                        onChange={changeProfileDetails}
                        placeholder="City"
                        className={`w-full px-3 py-2 text-xs sm:text-sm font-medium rounded-xl border ${cityErr ? 'border-rose-400' : 'border-slate-200'
                          } bg-slate-50/50 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]`}
                      />
                      {cityErr && <p className="text-[10px] text-rose-500 font-semibold m-0">{cityErr}</p>}
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-700 block">
                        State <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="state"
                        type="text"
                        name="state"
                        value={updateProfile.state}
                        onChange={changeProfileDetails}
                        placeholder="State"
                        className={`w-full px-3 py-2 text-xs sm:text-sm font-medium rounded-xl border ${stateErr ? 'border-rose-400' : 'border-slate-200'
                          } bg-slate-50/50 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]`}
                      />
                      {stateErr && <p className="text-[10px] text-rose-500 font-semibold m-0">{stateErr}</p>}
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-700 block">
                        Postal Code <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="pin_code"
                        type="text"
                        maxLength={6}
                        name="pin_code"
                        value={updateProfile.pin_code}
                        onChange={changeProfileDetails}
                        placeholder="123456"
                        className={`w-full px-3 py-2 text-xs sm:text-sm font-medium rounded-xl border ${pinCodeErr ? 'border-rose-400' : 'border-slate-200'
                          } bg-slate-50/50 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]`}
                      />
                      {pinCodeErr && (
                        <p className="text-[10px] text-rose-500 font-semibold m-0">{pinCodeErr}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Identity Documents Compliance Section */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 space-y-4">
            <div className="flex items-center gap-2.5 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center">
                <FileText className="w-4 h-4 text-[#1AA7DE]" />
              </div>
              <div>
                <h3 className="text-base font-black text-slate-900 m-0">
                  Identity Verification Documents
                </h3>
                <span className="text-xs text-slate-400 font-medium">
                  Upload government identity card (front & back sides) for admin verification
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {documentTypes.map((docType) => {
                const existingDoc = (authState as any)?.documents?.find((d: any) => d.type === docType.type);
                const docId = existingDoc?._id || docType.type;
                const hasFront = newDocumentFiles[docId]?.front;
                const hasBack = newDocumentFiles[docId]?.back;

                return (
                  <div key={docType.type} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider block">
                      {docType.label}
                    </span>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Front Upload */}
                      <div className="space-y-1">
                        <span className="text-[11px] font-bold text-slate-500 block text-center">
                          Front Side
                        </span>
                        <label className="block cursor-pointer">
                          {hasFront ? (
                            <div className="relative h-32 rounded-xl border-2 border-emerald-400 overflow-hidden bg-white shadow-2xs">
                              <img
                                src={URL.createObjectURL(hasFront)}
                                alt="Front"
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeNewDocument(docId, 'front');
                                }}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-xs hover:bg-rose-600 transition-colors cursor-pointer"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : existingDoc?.images?.front &&
                            !removedDocuments.includes(`${existingDoc._id}-front`) ? (
                            <div className="relative h-32 rounded-xl border border-slate-200 overflow-hidden bg-white shadow-2xs">
                              <img
                                src={getDocumentImageUrl(existingDoc.images.front)}
                                alt="Front"
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeExistingDocument(existingDoc._id, 'front');
                                }}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-xs hover:bg-rose-600 transition-colors cursor-pointer"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="h-32 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#1AA7DE] bg-white flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-[#1AA7DE] transition-all">
                              <Upload className="w-5 h-5" />
                              <span className="text-[11px] font-bold">Upload Front</span>
                            </div>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) =>
                              e.target.files && handleDocumentUpload(docId, 'front', e.target.files[0])
                            }
                          />
                        </label>
                      </div>

                      {/* Back Upload */}
                      <div className="space-y-1">
                        <span className="text-[11px] font-bold text-slate-500 block text-center">
                          Back Side
                        </span>
                        <label className="block cursor-pointer">
                          {hasBack ? (
                            <div className="relative h-32 rounded-xl border-2 border-emerald-400 overflow-hidden bg-white shadow-2xs">
                              <img
                                src={URL.createObjectURL(hasBack)}
                                alt="Back"
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeNewDocument(docId, 'back');
                                }}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-xs hover:bg-rose-600 transition-colors cursor-pointer"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : existingDoc?.images?.back &&
                            !removedDocuments.includes(`${existingDoc._id}-back`) ? (
                            <div className="relative h-32 rounded-xl border border-slate-200 overflow-hidden bg-white shadow-2xs">
                              <img
                                src={getDocumentImageUrl(existingDoc.images.back)}
                                alt="Back"
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeExistingDocument(existingDoc._id, 'back');
                                }}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-xs hover:bg-rose-600 transition-colors cursor-pointer"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="h-32 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#1AA7DE] bg-white flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-[#1AA7DE] transition-all">
                              <Upload className="w-5 h-5" />
                              <span className="text-[11px] font-bold">Upload Back</span>
                            </div>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) =>
                              e.target.files && handleDocumentUpload(docId, 'back', e.target.files[0])
                            }
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form Action Controls */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/profile')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
              className="px-6 min-w-[140px]"
            >
              <Save className="w-4 h-4 me-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </Fragment>
  );
};

export default EditProfile;